import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import Header, { useBu } from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { toLocalDateStr } from '../utils/formatters';
import {
  useEmployees,
  useHSRecords,
  usePerformancePlans,
  useYearlyReviews,
  useOnoItems,
  useOnoSessions,
} from '../hooks/useTeams';

const TABS = ['Employees', 'Health & Safety', 'Performance Plans', 'Yearly Reviews', '1-on-1s'];
const STATUS_COLORS = { active: '#22c55e', completed: '#6366f1', cancelled: '#94a3b8', open: '#f59e0b', closed: '#22c55e', overdue: '#ef4444' };

// ── Employees Tab ─────────────────────────────────────────────────────────────
function EmployeesTab({ user, businessUnits, buId }) {
  const { employees, loading, error, create, update, remove } = useEmployees(buId);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', department: '', position: '', start_date: '', business_unit_id: '', manager_id: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [managers, setManagers] = useState([]);
  const canEdit = ['admin', 'manager'].includes(user?.role);

  // Load users who can be managers
  useEffect(() => {
    apiClient.get('/users').then(r => {
      const users = r.data.users || r.data || [];
      setManagers(users.filter(u => ['admin', 'manager'].includes(u.role)));
    }).catch(() => {});
  }, []);

  const resetForm = () => { setForm({ name: '', department: '', position: '', start_date: '', business_unit_id: '', manager_id: '' }); setErr(''); };
  const openAdd = () => { resetForm(); setEditing(null); setShowForm(true); };
  const openEdit = (emp) => {
    setForm({
      name: emp.name,
      department: emp.department || '',
      position: emp.position || '',
      start_date: emp.start_date || '',
      business_unit_id: emp.business_unit_id || '',
      manager_id: emp.manager_id || '',
    });
    setEditing(emp);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      if (editing) await update(editing.id, form);
      else await create(form);
      setShowForm(false); resetForm(); setEditing(null);
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const activeBUs = businessUnits.filter(bu => bu.active);

  return (
    <div style={s.tab}>
      {error && <ErrorBanner message={error} />}
      {canEdit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button onClick={openAdd} style={s.addBtn}>+ Add Employee</button>
        </div>
      )}

      {showForm && (
        <div style={s.formCard}>
          <h4 style={s.formTitle}>{editing ? 'Edit Employee' : 'New Employee'}</h4>
          {err && <div style={s.formErr}>{err}</div>}
          <form onSubmit={handleSubmit} style={s.formGrid}>
            <div style={s.ff}>
              <label style={s.label}>Full Name *</label>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={s.input} placeholder="Jane Smith" />
            </div>
            <div style={s.ff}>
              <label style={s.label}>Department</label>
              <input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} style={s.input} placeholder="Operations" />
            </div>
            <div style={s.ff}>
              <label style={s.label}>Position</label>
              <input value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} style={s.input} placeholder="Technician" />
            </div>
            <div style={s.ff}>
              <label style={s.label}>Start Date</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={s.input} />
            </div>
            <div style={s.ff}>
              <label style={s.label}>Business Unit</label>
              <select value={form.business_unit_id} onChange={e => setForm(f => ({ ...f, business_unit_id: e.target.value }))} style={s.input}>
                <option value="">— None —</option>
                {activeBUs.map(bu => (
                  <option key={bu.id} value={bu.id}>{bu.name}</option>
                ))}
              </select>
            </div>
            <div style={s.ff}>
              <label style={s.label}>Manager</label>
              <select value={form.manager_id} onChange={e => setForm(f => ({ ...f, manager_id: e.target.value }))} style={s.input}>
                <option value="">— None —</option>
                {managers.map(m => (
                  <option key={m.id} value={m.id}>{m.name || m.email}</option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: '1/-1', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); resetForm(); }} style={s.cancelBtn}>Cancel</button>
              <button type="submit" disabled={saving} style={s.addBtn}>{saving ? 'Saving…' : editing ? 'Update' : 'Add Employee'}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead><tr>
              {['Name', 'Business Unit', 'Department', 'Position', 'Manager', 'Start Date', 'Status', 'Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {employees.length === 0 ? (
                <tr><td colSpan={8} style={s.empty}>No employees yet</td></tr>
              ) : employees.map((emp, i) => (
                <tr key={emp.id} style={i % 2 !== 0 ? { background: '#f8fafc' } : {}}>
                  <td style={s.td}><strong>{emp.name}</strong></td>
                  <td style={s.td}>
                    {emp.business_unit_name
                      ? <span style={s.buBadge}>{emp.business_unit_name}</span>
                      : <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>}
                  </td>
                  <td style={s.td}>{emp.department || '—'}</td>
                  <td style={s.td}>{emp.position || '—'}</td>
                  <td style={s.td}>{emp.manager_name || '—'}</td>
                  <td style={s.td}>{emp.start_date || '—'}</td>
                  <td style={s.td}>
                    <span style={{ color: emp.active ? '#22c55e' : '#94a3b8', fontWeight: '600', fontSize: '0.8rem' }}>
                      {emp.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={s.td}>
                    {canEdit && (
                      <>
                        <button onClick={() => openEdit(emp)} style={s.actionBtn}>Edit</button>
                        <button onClick={() => update(emp.id, { active: emp.active ? 0 : 1 })} style={{ ...s.actionBtn, color: emp.active ? '#f59e0b' : '#22c55e' }}>
                          {emp.active ? 'Deactivate' : 'Activate'}
                        </button>
                        {user?.role === 'admin' && (
                          <button onClick={() => { if (window.confirm('Delete employee?')) remove(emp.id); }} style={{ ...s.actionBtn, color: '#ef4444' }}>Delete</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── H&S Tab ────────────────────────────────────────────────────────────────────
function HSTab({ user, buId }) {
  const { records, loading, error, create, update, remove } = useHSRecords(buId);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'training', employee_name: '', description: '', status: 'open', due_date: '', completed_date: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const canEdit = ['admin', 'manager'].includes(user?.role);

  const compliant = records.filter(r => r.status === 'closed').length;
  const pct = records.length ? Math.round(compliant / records.length * 100) : 100;

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await create(form);
      setShowForm(false);
      setForm({ type: 'training', employee_name: '', description: '', status: 'open', due_date: '', completed_date: '' });
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={s.tab}>
      {error && <ErrorBanner message={error} />}

      <div style={s.kpiRow}>
        <div style={s.kpiCard}>
          <div style={{ fontSize: '2rem', fontWeight: '800', color: pct >= 80 ? '#22c55e' : '#ef4444' }}>{pct}%</div>
          <div style={s.kpiLabel}>Compliance Rate</div>
        </div>
        <div style={s.kpiCard}>
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1e293b' }}>{records.filter(r => r.status === 'open').length}</div>
          <div style={s.kpiLabel}>Open Items</div>
        </div>
        <div style={s.kpiCard}>
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#ef4444' }}>{records.filter(r => r.status === 'overdue').length}</div>
          <div style={s.kpiLabel}>Overdue</div>
        </div>
        <div style={s.kpiCard}>
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1e293b' }}>{records.length}</div>
          <div style={s.kpiLabel}>Total Records</div>
        </div>
      </div>

      {canEdit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button onClick={() => setShowForm(v => !v)} style={s.addBtn}>{showForm ? 'Cancel' : '+ Add Record'}</button>
        </div>
      )}

      {showForm && (
        <div style={s.formCard}>
          {err && <div style={s.formErr}>{err}</div>}
          <form onSubmit={handleSubmit} style={s.formGrid}>
            <div style={s.ff}>
              <label style={s.label}>Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={s.input}>
                <option value="training">Training</option>
                <option value="incident">Incident</option>
                <option value="compliance">Compliance</option>
                <option value="inspection">Inspection</option>
              </select>
            </div>
            <div style={s.ff}>
              <label style={s.label}>Employee Name</label>
              <input value={form.employee_name} onChange={e => setForm(f => ({ ...f, employee_name: e.target.value }))} style={s.input} placeholder="Jane Smith" />
            </div>
            <div style={{ ...s.ff, gridColumn: '1/-1' }}>
              <label style={s.label}>Description</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={s.input} placeholder="Brief description" />
            </div>
            <div style={s.ff}>
              <label style={s.label}>Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={s.input}>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div style={s.ff}>
              <label style={s.label}>Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} style={s.input} />
            </div>
            <div style={s.ff}>
              <label style={s.label}>Completed Date</label>
              <input type="date" value={form.completed_date} onChange={e => setForm(f => ({ ...f, completed_date: e.target.value }))} style={s.input} />
            </div>
            <div style={{ gridColumn: '1/-1', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button type="button" onClick={() => setShowForm(false)} style={s.cancelBtn}>Cancel</button>
              <button type="submit" disabled={saving} style={s.addBtn}>{saving ? 'Saving…' : 'Add Record'}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead><tr>
              {['Type', 'Employee', 'Description', 'Status', 'Due Date', 'Completed', 'Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {records.length === 0 ? (
                <tr><td colSpan={7} style={s.empty}>No H&S records</td></tr>
              ) : records.map((r, i) => (
                <tr key={r.id} style={i % 2 !== 0 ? { background: '#f8fafc' } : {}}>
                  <td style={s.td}><span style={{ textTransform: 'capitalize' }}>{r.type}</span></td>
                  <td style={s.td}>{r.employee_name || '—'}</td>
                  <td style={s.td}>{r.description || '—'}</td>
                  <td style={s.td}>
                    <span style={{ color: STATUS_COLORS[r.status] || '#94a3b8', fontWeight: '600', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                      {r.status}
                    </span>
                  </td>
                  <td style={s.td}>{r.due_date || '—'}</td>
                  <td style={s.td}>{r.completed_date || '—'}</td>
                  <td style={s.td}>
                    {canEdit && (
                      <>
                        {r.status !== 'closed' && (
                          <button onClick={() => update(r.id, { status: 'closed', completed_date: toLocalDateStr(new Date()) })} style={{ ...s.actionBtn, color: '#22c55e' }}>Close</button>
                        )}
                        {user?.role === 'admin' && (
                          <button onClick={() => { if (window.confirm('Delete record?')) remove(r.id); }} style={{ ...s.actionBtn, color: '#ef4444' }}>Delete</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Performance Plans Tab ──────────────────────────────────────────────────────
function PerformancePlansTab({ user, buId }) {
  const { plans, loading, error, create, update, remove } = usePerformancePlans(buId);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ employee_name: '', title: '', goals: '', status: 'active', start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const canEdit = ['admin', 'manager'].includes(user?.role);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const goals = form.goals.split('\n').map(g => g.trim()).filter(Boolean);
      await create({ ...form, goals: JSON.stringify(goals) });
      setShowForm(false);
      setForm({ employee_name: '', title: '', goals: '', status: 'active', start_date: '', end_date: '' });
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const parseGoals = (goalsStr) => {
    try { return typeof goalsStr === 'string' ? JSON.parse(goalsStr) : []; }
    catch { return []; }
  };

  return (
    <div style={s.tab}>
      {error && <ErrorBanner message={error} />}
      {canEdit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button onClick={() => setShowForm(v => !v)} style={s.addBtn}>{showForm ? 'Cancel' : '+ Add Plan'}</button>
        </div>
      )}

      {showForm && (
        <div style={s.formCard}>
          {err && <div style={s.formErr}>{err}</div>}
          <form onSubmit={handleSubmit} style={s.formGrid}>
            <div style={s.ff}>
              <label style={s.label}>Employee Name *</label>
              <input required value={form.employee_name} onChange={e => setForm(f => ({ ...f, employee_name: e.target.value }))} style={s.input} />
            </div>
            <div style={s.ff}>
              <label style={s.label}>Plan Title *</label>
              <input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={s.input} />
            </div>
            <div style={s.ff}>
              <label style={s.label}>Start Date</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={s.input} />
            </div>
            <div style={s.ff}>
              <label style={s.label}>End Date</label>
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} style={s.input} />
            </div>
            <div style={{ ...s.ff, gridColumn: '1/-1' }}>
              <label style={s.label}>Goals (one per line)</label>
              <textarea value={form.goals} onChange={e => setForm(f => ({ ...f, goals: e.target.value }))} style={{ ...s.input, minHeight: '80px', resize: 'vertical' }} placeholder="Improve response time&#10;Complete safety training&#10;Hit 95% customer satisfaction" />
            </div>
            <div style={{ gridColumn: '1/-1', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button type="button" onClick={() => setShowForm(false)} style={s.cancelBtn}>Cancel</button>
              <button type="submit" disabled={saving} style={s.addBtn}>{saving ? 'Saving…' : 'Create Plan'}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {plans.length === 0 ? (
            <div style={s.empty}>No performance plans yet</div>
          ) : plans.map(plan => {
            const goals = parseGoals(plan.goals);
            const isExp = expanded === plan.id;
            return (
              <div key={plan.id} style={s.planCard}>
                <div style={s.planHeader} onClick={() => setExpanded(isExp ? null : plan.id)}>
                  <div>
                    <strong style={{ fontSize: '0.95rem' }}>{plan.title}</strong>
                    <span style={{ color: '#64748b', fontSize: '0.8rem', marginLeft: '0.75rem' }}>{plan.employee_name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ color: STATUS_COLORS[plan.status] || '#94a3b8', fontWeight: '600', fontSize: '0.8rem', textTransform: 'capitalize', background: '#f8fafc', padding: '0.2rem 0.5rem', borderRadius: '0.25rem' }}>{plan.status}</span>
                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{isExp ? '▲' : '▼'}</span>
                  </div>
                </div>
                {isExp && (
                  <div style={{ padding: '1rem', borderTop: '1px solid #f1f5f9' }}>
                    {plan.start_date && <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#64748b' }}>Period: {plan.start_date} — {plan.end_date || 'ongoing'}</p>}
                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>Goals ({goals.length}):</p>
                    <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', color: '#475569' }}>
                      {goals.map((g, gi) => <li key={gi} style={{ marginBottom: '0.25rem' }}>{g}</li>)}
                    </ul>
                    {canEdit && (
                      <div style={{ marginTop: '0.875rem', display: 'flex', gap: '0.5rem' }}>
                        {plan.status === 'active' && <button onClick={() => update(plan.id, { status: 'completed' })} style={{ ...s.actionBtn, color: '#6366f1' }}>Mark Complete</button>}
                        {plan.status === 'active' && <button onClick={() => update(plan.id, { status: 'cancelled' })} style={{ ...s.actionBtn, color: '#f59e0b' }}>Cancel Plan</button>}
                        {user?.role === 'admin' && <button onClick={() => { if (window.confirm('Delete plan?')) remove(plan.id); }} style={{ ...s.actionBtn, color: '#ef4444' }}>Delete</button>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Yearly Reviews Tab ─────────────────────────────────────────────────────────
function YearlyReviewsTab({ user, buId }) {
  const { reviews, loading, error, create } = useYearlyReviews(buId);
  const [showForm, setShowForm] = useState(false);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({ employee_name: '', review_year: new Date().getFullYear(), rating: 3, strengths: '', improvements: '', goals_next: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const canEdit = ['admin', 'manager'].includes(user?.role);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const filtered = reviews.filter(r => !filterYear || r.review_year === filterYear);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await create(form);
      setShowForm(false);
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const Stars = ({ rating }) => (
    <span>
      {[1, 2, 3, 4, 5].map(n => (
        <span key={n} style={{ color: n <= rating ? '#f59e0b' : '#e2e8f0', fontSize: '1.1rem' }}>★</span>
      ))}
    </span>
  );

  return (
    <div style={s.tab}>
      {error && <ErrorBanner message={error} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>
          <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginRight: '0.5rem' }}>Year:</label>
          <select value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} style={{ ...s.input, width: 'auto', padding: '0.375rem 0.625rem' }}>
            <option value={0}>All Years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {canEdit && <button onClick={() => setShowForm(v => !v)} style={s.addBtn}>{showForm ? 'Cancel' : '+ Add Review'}</button>}
      </div>

      {showForm && (
        <div style={s.formCard}>
          {err && <div style={s.formErr}>{err}</div>}
          <form onSubmit={handleSubmit} style={s.formGrid}>
            <div style={s.ff}>
              <label style={s.label}>Employee Name *</label>
              <input required value={form.employee_name} onChange={e => setForm(f => ({ ...f, employee_name: e.target.value }))} style={s.input} />
            </div>
            <div style={s.ff}>
              <label style={s.label}>Review Year</label>
              <select value={form.review_year} onChange={e => setForm(f => ({ ...f, review_year: Number(e.target.value) }))} style={s.input}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div style={s.ff}>
              <label style={s.label}>Rating (1-5)</label>
              <select value={form.rating} onChange={e => setForm(f => ({ ...f, rating: Number(e.target.value) }))} style={s.input}>
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} Star{n > 1 ? 's' : ''}</option>)}
              </select>
            </div>
            <div style={{ ...s.ff, gridColumn: '1/-1' }}>
              <label style={s.label}>Strengths</label>
              <textarea value={form.strengths} onChange={e => setForm(f => ({ ...f, strengths: e.target.value }))} style={{ ...s.input, minHeight: '60px', resize: 'vertical' }} />
            </div>
            <div style={{ ...s.ff, gridColumn: '1/-1' }}>
              <label style={s.label}>Areas for Improvement</label>
              <textarea value={form.improvements} onChange={e => setForm(f => ({ ...f, improvements: e.target.value }))} style={{ ...s.input, minHeight: '60px', resize: 'vertical' }} />
            </div>
            <div style={{ ...s.ff, gridColumn: '1/-1' }}>
              <label style={s.label}>Goals for Next Year</label>
              <textarea value={form.goals_next} onChange={e => setForm(f => ({ ...f, goals_next: e.target.value }))} style={{ ...s.input, minHeight: '60px', resize: 'vertical' }} />
            </div>
            <div style={{ gridColumn: '1/-1', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button type="button" onClick={() => setShowForm(false)} style={s.cancelBtn}>Cancel</button>
              <button type="submit" disabled={saving} style={s.addBtn}>{saving ? 'Saving…' : 'Save Review'}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.length === 0 ? (
            <div style={s.empty}>No reviews for {filterYear || 'any year'}</div>
          ) : filtered.map(rev => (
            <div key={rev.id} style={s.planCard}>
              <div style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div>
                    <strong style={{ fontSize: '0.95rem' }}>{rev.employee_name}</strong>
                    <span style={{ color: '#64748b', fontSize: '0.8rem', marginLeft: '0.75rem' }}>{rev.review_year}</span>
                  </div>
                  <Stars rating={rev.rating} />
                </div>
                {rev.strengths && <p style={s.reviewSection}><span style={s.reviewLabel}>Strengths:</span> {rev.strengths}</p>}
                {rev.improvements && <p style={s.reviewSection}><span style={s.reviewLabel}>Improvements:</span> {rev.improvements}</p>}
                {rev.goals_next && <p style={s.reviewSection}><span style={s.reviewLabel}>Next Year Goals:</span> {rev.goals_next}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 1-on-1s Tab ───────────────────────────────────────────────────────────────
function OnoTab({ user, buId }) {
  const { items, create: createItem } = useOnoItems();
  const { sessions, stats, loading, create: createSession } = useOnoSessions(buId);
  const { employees } = useEmployees(buId);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [sessionDate, setSessionDate] = useState(toLocalDateStr(new Date()));
  const [sessionNotes, setSessionNotes] = useState('');
  const [newItemLabel, setNewItemLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const canEdit = ['admin', 'manager'].includes(user?.role);

  const empSessions = sessions.filter(ss => ss.employee_id === Number(selectedEmp) || ss.employee_name === selectedEmp);

  const handleSessionSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const checked = Object.entries(checkedItems).filter(([, v]) => v).map(([k]) => Number(k));
      const emp = employees.find(e => e.id === Number(selectedEmp));
      await createSession({
        employee_id: Number(selectedEmp),
        employee_name: emp?.name || '',
        session_date: sessionDate,
        notes: sessionNotes,
        checked_items: checked,
      });
      setShowSessionForm(false);
      setCheckedItems({});
      setSessionNotes('');
    } catch (e) {
      // ignore
    } finally { setSaving(false); }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemLabel.trim()) return;
    await createItem({ label: newItemLabel.trim() });
    setNewItemLabel('');
    setShowItemForm(false);
  };

  return (
    <div style={s.tab}>
      <div style={s.kpiRow}>
        <div style={s.kpiCard}>
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#6366f1' }}>{stats.weeklyTotal}</div>
          <div style={s.kpiLabel}>This Week</div>
        </div>
        <div style={s.kpiCard}>
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1e293b' }}>{stats.ytdTotal}</div>
          <div style={s.kpiLabel}>Year-to-Date</div>
        </div>
        <div style={s.kpiCard}>
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1e293b' }}>{stats.employees?.length || 0}</div>
          <div style={s.kpiLabel}>Employees Tracked</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
        <div>
          <label style={{ ...s.label, display: 'block', marginBottom: '0.25rem' }}>Select Employee</label>
          <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)} style={{ ...s.input, width: 'auto', minWidth: '200px' }}>
            <option value="">— Select Employee —</option>
            {employees.filter(e => e.active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        {canEdit && selectedEmp && (
          <button onClick={() => setShowSessionForm(v => !v)} style={s.addBtn}>
            {showSessionForm ? 'Cancel' : '+ New Session'}
          </button>
        )}
        {user?.role === 'admin' && (
          <button onClick={() => setShowItemForm(v => !v)} style={{ ...s.addBtn, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>
            {showItemForm ? 'Cancel' : '⚙ Manage Checklist'}
          </button>
        )}
      </div>

      {showItemForm && (
        <div style={s.formCard}>
          <h4 style={s.formTitle}>Checklist Items</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.75rem' }}>
            {items.map(it => (
              <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                <span style={{ color: it.active ? '#22c55e' : '#94a3b8' }}>●</span>
                {it.label}
                {it.active ? ' (active)' : ' (hidden)'}
              </div>
            ))}
          </div>
          <form onSubmit={handleAddItem} style={{ display: 'flex', gap: '0.5rem' }}>
            <input value={newItemLabel} onChange={e => setNewItemLabel(e.target.value)} style={{ ...s.input, flex: 1 }} placeholder="New checklist item…" />
            <button type="submit" style={s.addBtn}>Add</button>
          </form>
        </div>
      )}

      {showSessionForm && selectedEmp && (
        <div style={s.formCard}>
          <h4 style={s.formTitle}>New 1-on-1 Session</h4>
          <form onSubmit={handleSessionSubmit}>
            <div style={{ marginBottom: '0.875rem' }}>
              <label style={s.label}>Session Date</label>
              <input type="date" value={sessionDate} onChange={e => setSessionDate(e.target.value)} style={{ ...s.input, width: 'auto' }} />
            </div>
            <div style={{ marginBottom: '0.875rem' }}>
              <label style={s.label}>Checklist</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginTop: '0.25rem' }}>
                {items.filter(it => it.active).map(it => (
                  <label key={it.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!checkedItems[it.id]}
                      onChange={e => setCheckedItems(c => ({ ...c, [it.id]: e.target.checked }))}
                    />
                    {it.label}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: '0.875rem' }}>
              <label style={s.label}>Notes</label>
              <textarea value={sessionNotes} onChange={e => setSessionNotes(e.target.value)} style={{ ...s.input, minHeight: '60px', resize: 'vertical', width: '100%' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowSessionForm(false)} style={s.cancelBtn}>Cancel</button>
              <button type="submit" disabled={saving} style={s.addBtn}>{saving ? 'Saving…' : 'Save Session'}</button>
            </div>
          </form>
        </div>
      )}

      {selectedEmp && (
        loading ? <LoadingSpinner /> : (
          <div>
            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: '700', color: '#374151' }}>
              Sessions ({empSessions.length})
            </h4>
            {empSessions.length === 0 ? (
              <div style={s.empty}>No sessions recorded for this employee</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {empSessions.slice().reverse().map(sess => {
                  let checked = [];
                  try { checked = JSON.parse(sess.checked_items || '[]'); } catch { }
                  return (
                    <div key={sess.id} style={{ ...s.planCard, padding: '0.875rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong style={{ fontSize: '0.875rem' }}>{sess.session_date}</strong>
                        <span style={{ fontSize: '0.8rem', color: '#6366f1', fontWeight: '600' }}>{checked.length}/{items.filter(i => i.active).length} items</span>
                      </div>
                      {sess.notes && <p style={{ margin: '0.375rem 0 0 0', fontSize: '0.8rem', color: '#64748b' }}>{sess.notes}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )
      )}

      {!selectedEmp && !loading && (
        <div style={s.empty}>Select an employee above to view their 1-on-1 sessions</div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function TeamsPage() {
  const { user } = useAuth();
  const { selectedBu } = useBu();
  const [activeTab, setActiveTab] = useState(0);
  const [businessUnits, setBusinessUnits] = useState([]);

  useEffect(() => {
    apiClient.get('/business-units')
      .then(r => setBusinessUnits(r.data.units || []))
      .catch(() => {});
  }, []);

  return (
    <div>
      <Header title="Teams" />
      <div style={s.page}>
        <div style={s.tabBar}>
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              style={{ ...s.tabBtn, ...(activeTab === i ? s.tabBtnActive : {}) }}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 0 && <EmployeesTab user={user} businessUnits={businessUnits} buId={selectedBu} />}
        {activeTab === 1 && <HSTab user={user} buId={selectedBu} />}
        {activeTab === 2 && <PerformancePlansTab user={user} buId={selectedBu} />}
        {activeTab === 3 && <YearlyReviewsTab user={user} buId={selectedBu} />}
        {activeTab === 4 && <OnoTab user={user} buId={selectedBu} />}
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const s = {
  page: { padding: '1.5rem 1.75rem' },
  tabBar: { display: 'flex', gap: '0.25rem', marginBottom: '1.25rem', background: '#fff', padding: '0.375rem', borderRadius: '0.625rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
  tabBtn: { padding: '0.5rem 1rem', background: 'transparent', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', color: '#64748b', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  tabBtnActive: { background: '#6366f1', color: '#fff' },
  tab: { background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  kpiRow: { display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' },
  kpiCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', padding: '1rem 1.5rem', minWidth: '120px', textAlign: 'center', flex: '1 1 100px' },
  kpiLabel: { fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem', fontWeight: '500' },
  addBtn: { padding: '0.5rem 1.125rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600' },
  cancelBtn: { padding: '0.5rem 1rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', color: '#475569' },
  actionBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: '0.8rem', fontWeight: '600', padding: '0 0.375rem' },
  formCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.625rem', padding: '1.25rem', marginBottom: '1.25rem' },
  formTitle: { margin: '0 0 0.875rem 0', fontSize: '0.9rem', fontWeight: '700', color: '#1e293b' },
  formErr: { background: '#fef2f2', color: '#dc2626', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.8rem', marginBottom: '0.75rem', border: '1px solid #fecaca' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.875rem' },
  ff: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  label: { fontSize: '0.8rem', fontWeight: '600', color: '#374151' },
  input: { padding: '0.5rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { padding: '0.625rem 0.75rem', background: '#f8fafc', color: '#64748b', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.625rem 0.75rem', borderBottom: '1px solid #f1f5f9', color: '#1e293b', verticalAlign: 'middle' },
  empty: { padding: '2rem', textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: '0.5rem' },
  planCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.625rem', overflow: 'hidden' },
  planHeader: { padding: '0.875rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  reviewSection: { margin: '0 0 0.375rem 0', fontSize: '0.875rem', color: '#475569' },
  reviewLabel: { fontWeight: '600', color: '#374151' },
  buBadge: { display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '600', background: '#ede9fe', color: '#6d28d9' },
};
